import { Link } from '@tanstack/react-router';
import { MonitorIcon, SquareKanbanIcon, WaypointsIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import type { WorkflowView } from '@/lib/api/generated/model';
import { UserAvatar } from '@/shared/components/UserAvatar';

import { PALETTE_ITEMS } from './paletteItems';
import { ACCESS_COPY } from '@/features/workspaces/accessCopy';

/**
 * The builder at phone width: the workflow, read, not edited (spec 183).
 *
 * The editor puts a palette, a canvas and an inspector side by side, and on a
 * phone that ran the toolbar off the screen and cut the inspector off at the
 * right edge. Editing a graph by touch at that size is not worth the trade, so
 * the phone gets what it can do well: the phases in order, each step's name and
 * kind, and whom a gate goes to, with the way to the board and to the diagram.
 */
export function BuilderPhoneView({
  workspaceId,
  workflow,
  readOnly = false,
}: {
  workspaceId: string;
  workflow: WorkflowView;
  /**
   * Shown to someone who may only read the workspace, at any width (spec 189):
   * the same read of the workflow, with the reason that is actually theirs.
   */
  readOnly?: boolean;
}) {
  const phases = workflow.phases.filter(
    (phase) => (phase.steps ?? []).length > 0,
  );

  return (
    <div className="flex flex-col gap-5 px-4 py-6">
      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-foreground text-xl font-semibold">
            {workflow.name}
          </h1>
          {workflow.status === 'draft' ? (
            <Badge variant="outline">Draft</Badge>
          ) : null}
        </div>
        <p className="text-muted-foreground flex items-start gap-2 text-sm">
          <MonitorIcon
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0"
            strokeWidth={1.5}
          />
          <span>
            {readOnly
              ? ACCESS_COPY.membersOnly
              : 'Editing a workflow needs a larger screen.'}{' '}
            {workflow.status === 'draft'
              ? 'This one is not published yet.'
              : workflow.has_pending_changes
                ? 'It has unpublished changes, not shown here.'
                : null}
          </span>
        </p>
        {/* The two ways out share the width (spec 273). */}
        <div className="flex flex-wrap gap-2 max-md:[&>*]:flex-1">
          {workflow.status === 'published' ? (
            <Link
              to="/workspaces/$workspaceId/workflows/$workflowKey/board"
              params={{ workspaceId, workflowKey: workflow.key }}
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
            >
              <SquareKanbanIcon aria-hidden="true" strokeWidth={1.5} />
              View board
            </Link>
          ) : null}
          <Link
            to="/workspaces/$workspaceId/workflows/$workflowKey"
            params={{ workspaceId, workflowKey: workflow.key }}
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            <WaypointsIcon aria-hidden="true" strokeWidth={1.5} />
            View diagram
          </Link>
        </div>
      </header>

      {phases.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          This workflow has no steps yet.
        </p>
      ) : (
        <ol aria-label="Phases and steps" className="flex flex-col gap-5">
          {phases.map((phase) => (
            <li
              key={phase.id}
              aria-label={`${phase.name} phase`}
              className="flex flex-col gap-2"
            >
              <h2 className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
                {phase.name}
              </h2>
              <ol className="border-border bg-card divide-border divide-y rounded-lg border">
                {(phase.steps ?? []).map((step) => {
                  const kind = PALETTE_ITEMS.find(
                    (item) => item.type === step.type,
                  );
                  const Icon = kind?.Icon;
                  return (
                    <li
                      key={step.id}
                      aria-label={step.name}
                      className="flex items-center gap-3 px-3 py-2.5"
                    >
                      {Icon ? (
                        <Icon
                          aria-hidden="true"
                          className="text-muted-foreground size-4 shrink-0"
                          strokeWidth={1.75}
                        />
                      ) : null}
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="text-foreground truncate text-sm font-medium">
                          {step.name}
                        </span>
                        <span className="text-muted-foreground truncate text-xs">
                          {kind?.label}
                        </span>
                      </span>
                      {step.type === 'human_gate' && step.assignee ? (
                        <span className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-xs">
                          <UserAvatar
                            id={step.assignee.id}
                            displayName={step.assignee.display_name}
                            email={step.assignee.email}
                            className="size-5"
                          />
                          <span className="truncate">
                            {step.assignee.display_name}
                          </span>
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
